import { useMemo, useState } from 'react';

/**
 * Inventory filter panel — tree edition.
 *
 * Layout (top to bottom):
 *   - Tags row (chips). All tags shown when no category selected; when
 *     one or more categories are selected, only tags used by items in
 *     those categories are shown — EXCEPT a selected tag is always shown
 *     (never hide an active filter).
 *   - Category tree. Each category is a row with a disclosure triangle
 *     (expand/collapse subs) and a count. Subcategories nest beneath.
 *
 * Selection model (behavior (a), tree flavor):
 *   - Clicking a CATEGORY row selects all of its subcategories (and the
 *     category itself counts as selected). Clicking it again clears them.
 *   - With subs selected, clicking an individual SUB toggles just that one.
 *   - The disclosure triangle only expands/collapses — never selects.
 *   - Parent row highlights when ANY of its subs are selected.
 *   - A category with no subcategories is itself directly selectable.
 *
 * Counts are static: total items in that category (incl. all its subs)
 * or in that subcategory, regardless of other active filters.
 *
 * "Uncategorized" (items with no category) appears as a synthetic row at
 * the bottom. It has no subs and no delete; selecting it filters to
 * category-less items.
 *
 * Controlled: parent owns the selection Sets + handlers.
 */
export default function FilterPanel({
  items,
  selected,           // { categories:Set, subcategories:Set, tags:Set }
  onToggleCategory,   // (categoryName) => void   — selects/clears whole category (all subs)
  onToggleSub,        // (subName, parentCategory) => void  — toggles one subcategory
  onToggleTag,        // (tag) => void
  onClear,
}) {
  const [expanded, setExpanded] = useState(() => new Set());

  // Build the tree + counts + tag-by-category index from the items.
  const { tree, uncategorizedCount, allTags, tagsByCategory } = useMemo(() => {
    const catMap = new Map();      // category -> { count, subs: Map(sub -> count) }
    const tagSet = new Set();
    const tagsByCat = new Map();   // category -> Set(tag)
    let uncat = 0;

    for (const it of items) {
      for (const t of it.tags ?? []) tagSet.add(t);

      if (!it.category) { uncat += 1; continue; }

      if (!catMap.has(it.category)) catMap.set(it.category, { count: 0, subs: new Map() });
      const entry = catMap.get(it.category);
      entry.count += 1;
      if (it.subcategory) {
        entry.subs.set(it.subcategory, (entry.subs.get(it.subcategory) ?? 0) + 1);
      }

      if (!tagsByCat.has(it.category)) tagsByCat.set(it.category, new Set());
      for (const t of it.tags ?? []) tagsByCat.get(it.category).add(t);
    }

    const treeArr = [...catMap.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, { count, subs }]) => ({
        name,
        count,
        subs: [...subs.entries()]
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([subName, subCount]) => ({ name: subName, count: subCount })),
      }));

    return {
      tree: treeArr,
      uncategorizedCount: uncat,
      allTags: [...tagSet].sort(),
      tagsByCategory: tagsByCat,
    };
  }, [items]);

  // Which tags to show: all, unless categories are selected — then only
  // tags used in selected categories, plus any currently-selected tag.
  const visibleTags = useMemo(() => {
    if (selected.categories.size === 0) return allTags;
    const allowed = new Set();
    for (const cat of selected.categories) {
      const s = tagsByCategory.get(cat);
      if (s) for (const t of s) allowed.add(t);
    }
    // Never hide an active filter.
    for (const t of selected.tags) allowed.add(t);
    return allTags.filter((t) => allowed.has(t));
  }, [allTags, tagsByCategory, selected.categories, selected.tags]);

  const anySelected =
    selected.categories.size > 0 ||
    selected.subcategories.size > 0 ||
    selected.tags.size > 0;

  if (tree.length === 0 && uncategorizedCount === 0 && allTags.length === 0) {
    return null;
  }

  function toggleExpand(name) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  return (
    <div className="card filter-panel">
      <div className="row between mb-14">
        <span className="eyebrow">Filter</span>
        {anySelected && (
          <button
            type="button"
            onClick={onClear}
            className="muted tiny link-btn"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Tags above the tree */}
      {visibleTags.length > 0 && (
        <div className="mb-18">
          <div className="muted tiny mb-6">Tags</div>
          <div className="chips">
            {visibleTags.map((t) => {
              const on = selected.tags.has(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => onToggleTag(t)}
                  aria-pressed={on}
                className={`chip${on ? ' is-on' : ''}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Category tree */}
      {(tree.length > 0 || uncategorizedCount > 0) && (
        <div>
          <div className="muted tiny mb-6">Category</div>
          <div role="tree">
            {tree.map((cat) => {
              const subNames = cat.subs.map((s) => s.name);
              const anySubSelected = subNames.some((s) => selected.subcategories.has(s));
              const catSelected = selected.categories.has(cat.name) || anySubSelected;
              const isExpanded = expanded.has(cat.name);
              const hasSubs = cat.subs.length > 0;

              return (
                <div key={cat.name}>
                  <Row
                    depth={0}
                    label={cat.name}
                    count={cat.count}
                    selected={catSelected}
                    hasTriangle={hasSubs}
                    expanded={isExpanded}
                    onTriangle={() => toggleExpand(cat.name)}
                    onSelect={() => onToggleCategory(cat.name)}
                  />
                  {isExpanded && cat.subs.map((sub) => (
                    <Row
                      key={sub.name}
                      depth={1}
                      label={sub.name}
                      count={sub.count}
                      selected={selected.subcategories.has(sub.name)}
                      hasTriangle={false}
                      onSelect={() => onToggleSub(sub.name, cat.name)}
                    />
                  ))}
                </div>
              );
            })}

            {uncategorizedCount > 0 && (
              <Row
                depth={0}
                label="Uncategorized"
                count={uncategorizedCount}
                selected={selected.categories.has('__uncategorized__')}
                hasTriangle={false}
                muted
                onSelect={() => onToggleCategory('__uncategorized__')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ depth, label, count, selected, hasTriangle, expanded, onTriangle, onSelect, muted }) {
  return (
    <div className={`tree-row${depth ? ' is-nested' : ''}${selected ? ' is-on' : ''}`}>
      {/* Disclosure triangle (or spacer) */}
      {hasTriangle ? (
        <button
          type="button"
          onClick={onTriangle}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
          aria-expanded={!!expanded}
          className="tree-toggle"
        >
          {expanded ? '▾' : '▸'}
        </button>
      ) : (
        <span className="tree-spacer" />
      )}

      {/* Selectable label */}
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={`tree-label${selected ? ' is-on' : ''}${muted ? ' is-muted' : ''}`}
      >
        {label}
      </button>

      <span className="muted tiny tree-count">{count}</span>
    </div>
  );
}